import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategorias } from '../hooks/useCategorias';
import { useAuth } from '../context/AuthContext';
import { canUseFeature } from '../utils/planLimits';
import { ChevronDown, Plus, Check, X, Lock } from 'lucide-react';

export function CategorySelector({ tipo, value, onChange, className }) {
  const { user } = useAuth();
  const plan = user?.plan || 'gratuito';
  const canCreateCategories = canUseFeature(plan, 'customCategories');
  const navigate = useNavigate();
  const { categories, loading, addCategory } = useCategorias(tipo);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
        setNewName('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Focus input when creating
  useEffect(() => {
    if (creating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [creating]);

  const handleSelect = (cat) => {
    onChange(cat);
    setOpen(false);
    setCreating(false);
    setNewName('');
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const name = await addCategory(trimmed);
      onChange(name);
      setCreating(false);
      setNewName('');
      setOpen(false);
    } catch (error) {
      console.error('Failed to create category:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    } else if (e.key === 'Escape') {
      setCreating(false);
      setNewName('');
    }
  };

  if (loading) {
    return (
      <div className={`h-14 border-2 border-slate-200 rounded-lg flex items-center px-4 text-lg text-slate-400 ${className || ''}`}>
        Carregando...
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full h-14 border-2 border-slate-200 rounded-lg flex items-center justify-between px-4 text-lg bg-white hover:border-slate-400 transition-colors text-left"
        data-testid="category-selector-trigger"
      >
        <span className={value ? 'text-[#1B2A4A]' : 'text-slate-400'}>
          {value || 'Selecione uma categoria'}
        </span>
        <ChevronDown size={20} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => handleSelect(cat)}
              className={`w-full text-left px-4 py-3 text-lg hover:bg-slate-100 flex items-center justify-between transition-colors ${
                value === cat ? 'bg-slate-50 font-semibold text-[#1B2A4A]' : 'text-slate-700'
              }`}
            >
              {cat}
              {value === cat && <Check size={18} className="text-green-600" />}
            </button>
          ))}

          {/* Divider */}
          <div className="border-t border-slate-200" />

          {/* Create new */}
          {!canCreateCategories ? (
            <button
              type="button"
              onClick={() => navigate('/minha-conta')}
              className="w-full text-left px-4 py-3 text-base text-amber-700 hover:bg-amber-50 flex items-center gap-2 transition-colors"
            >
              <Lock size={18} />
              Upgrade para criar categorias
            </button>
          ) : creating ? (
            <div className="p-3 flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Nome da categoria"
                className="flex-1 h-12 px-3 border-2 border-slate-200 rounded-lg text-lg focus:outline-none focus:border-[#1B2A4A]"
                data-testid="new-category-input"
              />
              <button
                type="button"
                onClick={handleCreate}
                disabled={!newName.trim() || saving}
                className="w-12 h-12 bg-[#047857] hover:bg-[#059669] disabled:opacity-50 text-white rounded-lg flex items-center justify-center"
                data-testid="confirm-new-category-btn"
              >
                <Check size={20} />
              </button>
              <button
                type="button"
                onClick={() => { setCreating(false); setNewName(''); }}
                className="w-12 h-12 border-2 border-slate-200 text-slate-500 hover:bg-slate-100 rounded-lg flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="w-full text-left px-4 py-3 text-lg text-[#047857] font-semibold hover:bg-green-50 flex items-center gap-2 transition-colors"
              data-testid="create-category-btn"
            >
              <Plus size={20} />
              Criar nova categoria
            </button>
          )}
        </div>
      )}
    </div>
  );
}
